import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /**
   * Free-shipping threshold.
   * Shopify stores monetary values in minor units.
   * Rs 12,000 = 1,200,000 minor units.
   *
   * @type {number}
   */
  #freeShippingThreshold = 12000 * 100;

  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {MutationObserver | null} */
  #cartObserver = null;

  /** @type {number | null} */
  #shippingUpdateTimer = null;

  /** @type {AbortController | null} */
  #cartRequestController = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#handleDialogOpen
    );

    this.#addFreeShippingStyles();
    this.#observeCartChanges();
    this.#scheduleShippingUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleDialogOpen
    );

    this.#cartObserver?.disconnect();
    this.#cartObserver = null;

    this.#cartRequestController?.abort();
    this.#cartRequestController = null;

    if (this.#shippingUpdateTimer) {
      window.clearTimeout(this.#shippingUpdateTimer);
      this.#shippingUpdateTimer = null;
    }
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    /*
     * Allow the theme to finish updating the cart
     * before requesting the latest total.
     */
    this.#scheduleShippingUpdate(250);
  };

  #handleDialogOpen = () => {
    this.#updateStickyState();
    this.#scheduleShippingUpdate();
  };

  open() {
    this.showDialog();
    this.#scheduleShippingUpdate();

    /**
     * Close the cart drawer when the installments CTA
     * is clicked to prevent overlapping dialogs.
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent =
        document.querySelector('shopify-payment-terms')?.shadowRoot;

      const cta = installmentsContent?.querySelector(
        '#shopify-installments-cta'
      );

      cta?.addEventListener('click', this.closeDialog, {
        once: true,
      });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Watches the drawer for quantity, removal,
   * or product rendering changes.
   */
  #observeCartChanges() {
    this.#cartObserver?.disconnect();

    this.#cartObserver = new MutationObserver((mutations) => {
      const hasRelevantChange = mutations.some((mutation) => {
        const target =
          mutation.target instanceof Element
            ? mutation.target
            : mutation.target.parentElement;

        /*
         * Ignore changes made inside our shipping bar
         * to prevent an update loop.
         */
        return !target?.closest('.free-shipping-progress');
      });

      if (hasRelevantChange) {
        this.#scheduleShippingUpdate(180);
      }
    });

    this.#cartObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  /**
   * Debounces cart requests.
   *
   * @param {number} delay
   */
  #scheduleShippingUpdate(delay = 0) {
    if (this.#shippingUpdateTimer) {
      window.clearTimeout(this.#shippingUpdateTimer);
    }

    this.#shippingUpdateTimer = window.setTimeout(() => {
      this.#shippingUpdateTimer = null;
      this.#updateFreeShippingBar();
    }, delay);
  }

  /**
   * Gets the latest cart data and updates the bar.
   */
  async #updateFreeShippingBar() {
    const bar = this.#ensureFreeShippingBar();

    if (!bar) return;

    this.#cartRequestController?.abort();
    this.#cartRequestController = new AbortController();

    try {
      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart.js`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        signal: this.#cartRequestController.signal,
      });

      if (!response.ok) {
        throw new Error('Unable to load cart information.');
      }

      const cart = await response.json();
      const cartTotal = Number(cart.total_price) || 0;

      this.#renderFreeShippingBar(bar, cartTotal);
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === 'AbortError'
      ) {
        return;
      }

      console.error('Free shipping progress error:', error);
    }
  }

  /**
   * Creates the free-shipping bar.
   *
   * @returns {HTMLElement | null}
   */
  #ensureFreeShippingBar() {
    const { dialog } = /** @type {Refs} */ (this.refs);

    if (!dialog) return null;

    let bar = dialog.querySelector('.free-shipping-progress');

    if (bar) {
      return /** @type {HTMLElement} */ (bar);
    }

    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    if (!content) return null;

    bar = document.createElement('div');
    bar.className = 'free-shipping-progress';
    bar.setAttribute('role', 'status');
    bar.setAttribute('aria-live', 'polite');

    bar.innerHTML = `
      <div class="free-shipping-progress__top">
        <div
          class="free-shipping-progress__icon"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M3 6h11v10H3V6Z"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <path
              d="M14 10h3.4L21 13.5V16h-7v-6Z"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <circle
              cx="7"
              cy="18"
              r="2"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <circle
              cx="17.5"
              cy="18"
              r="2"
              stroke="currentColor"
              stroke-width="1.8"
            />
          </svg>
        </div>

        <div class="free-shipping-progress__message">
          Checking your free shipping progress...
        </div>
      </div>

      <div class="free-shipping-progress__details">
        <span>Shipping reward</span>
        <span>Goal: Rs 12,000</span>
      </div>

      <div
        class="free-shipping-progress__track"
        aria-hidden="true"
      >
        <span class="free-shipping-progress__fill">
          <span class="free-shipping-progress__shine"></span>
        </span>
      </div>
    `;

    content.insertAdjacentElement('beforebegin', bar);

    return bar;
  }

  /**
   * Updates the progress message and percentage.
   *
   * @param {HTMLElement} bar
   * @param {number} cartTotal
   */
  #renderFreeShippingBar(bar, cartTotal) {
    const message = bar.querySelector(
      '.free-shipping-progress__message'
    );

    const fill = bar.querySelector(
      '.free-shipping-progress__fill'
    );

    const icon = bar.querySelector(
      '.free-shipping-progress__icon'
    );

    if (!message || !fill) return;

    const remaining = Math.max(
      this.#freeShippingThreshold - cartTotal,
      0
    );

    const percentage = Math.min(
      (cartTotal / this.#freeShippingThreshold) * 100,
      100
    );

    fill.style.width = `${percentage}%`;

    if (remaining > 0) {
      bar.classList.remove(
        'free-shipping-progress--complete'
      );

      message.innerHTML = `
        <span class="free-shipping-progress__eyebrow">
          You're almost there!
        </span>

        <span class="free-shipping-progress__main-message">
          Add
          <strong class="free-shipping-progress__amount">
            ${this.#formatPKR(remaining)}
          </strong>
          more to unlock <strong>free shipping</strong>
        </span>
      `;

      if (icon) {
        icon.innerHTML = `
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M3 6h11v10H3V6Z"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <path
              d="M14 10h3.4L21 13.5V16h-7v-6Z"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <circle
              cx="7"
              cy="18"
              r="2"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <circle
              cx="17.5"
              cy="18"
              r="2"
              stroke="currentColor"
              stroke-width="1.8"
            />
          </svg>
        `;
      }
    } else {
      bar.classList.add(
        'free-shipping-progress--complete'
      );

      message.innerHTML = `
        <span class="free-shipping-progress__eyebrow">
          Congratulations!
        </span>

        <span class="free-shipping-progress__main-message">
          You've unlocked <strong>free shipping</strong>
        </span>
      `;

      if (icon) {
        icon.innerHTML = `
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M12 3v18M4 9h16v12H4V9Z"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <path
              d="M3 6h18v4H3V6Z"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <path
              d="M12 6c-3 0-5-1-5-3 3 0 5 1 5 3Zm0 0c3 0 5-1 5-3-3 0-5 1-5 3Z"
              stroke="currentColor"
              stroke-width="1.8"
            />
          </svg>
        `;
      }
    }
  }

  /**
   * Formats Shopify minor units as PKR.
   *
   * @param {number} amount
   * @returns {string}
   */
  #formatPKR(amount) {
    const formattedAmount = new Intl.NumberFormat('en-PK', {
      maximumFractionDigits: 0,
    }).format(amount / 100);

    return `Rs ${formattedAmount}`;
  }

  /**
   * Adds the free-shipping bar styles once.
   */
  #addFreeShippingStyles() {
    if (
      document.getElementById(
        'free-shipping-progress-styles'
      )
    ) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'free-shipping-progress-styles';

    style.textContent = `
      .free-shipping-progress {
        position: relative;
        margin: 14px 18px 16px;
        padding: 16px;
        overflow: hidden;
        background:
          radial-gradient(
            circle at top right,
            rgba(203, 134, 91, 0.18),
            transparent 42%
          ),
          linear-gradient(
            135deg,
            #fffaf6 0%,
            #f8eee7 100%
          );
        border: 1px solid rgba(151, 92, 58, 0.18);
        border-radius: 16px;
        box-shadow:
          0 8px 24px rgba(77, 42, 24, 0.08),
          inset 0 1px 0 rgba(255, 255, 255, 0.8);
      }

      .free-shipping-progress::after {
        content: "";
        position: absolute;
        top: -45px;
        right: -35px;
        width: 110px;
        height: 110px;
        background: rgba(189, 117, 73, 0.07);
        border-radius: 50%;
        pointer-events: none;
      }

      .free-shipping-progress__top {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .free-shipping-progress__icon {
        display: flex;
        flex: 0 0 42px;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
        color: #ffffff;
        background: linear-gradient(
          135deg,
          #a56743,
          #cf885d
        );
        border-radius: 12px;
        box-shadow:
          0 6px 14px rgba(145, 83, 48, 0.24);
        transform: rotate(-2deg);
        transition:
          transform 0.3s ease,
          background 0.3s ease;
      }

      .free-shipping-progress:hover
      .free-shipping-progress__icon {
        transform: translateY(-2px) rotate(0deg);
      }

      .free-shipping-progress__icon svg {
        width: 24px;
        height: 24px;
      }

      .free-shipping-progress__message {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: 2px;
        color: #2c211b;
        line-height: 1.4;
      }

      .free-shipping-progress__eyebrow {
        color: #9b603e;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }

      .free-shipping-progress__main-message {
        font-size: 13px;
        font-weight: 500;
      }

      .free-shipping-progress__main-message strong {
        color: #2b1b13;
        font-weight: 750;
      }

      .free-shipping-progress__amount {
        display: inline-block;
        color: #9b603e !important;
        white-space: nowrap;
      }

      .free-shipping-progress__details {
        position: relative;
        z-index: 1;
        display: flex;
        justify-content: space-between;
        gap: 12px;
        margin: 14px 1px 7px;
        color: #76665d;
        font-size: 10px;
        font-weight: 650;
        letter-spacing: 0.03em;
        text-transform: uppercase;
      }

      .free-shipping-progress__track {
        position: relative;
        z-index: 1;
        width: 100%;
        height: 9px;
        overflow: hidden;
        background: rgba(95, 65, 49, 0.12);
        border: 1px solid rgba(95, 65, 49, 0.05);
        border-radius: 999px;
        box-shadow:
          inset 0 1px 3px rgba(66, 39, 25, 0.08);
      }

      .free-shipping-progress__fill {
        position: relative;
        display: block;
        width: 0;
        height: 100%;
        overflow: hidden;
        background: linear-gradient(
          90deg,
          #8c5335 0%,
          #bd764e 55%,
          #df9b70 100%
        );
        border-radius: inherit;
        box-shadow:
          0 0 12px rgba(189, 118, 78, 0.35);
        transition:
          width 0.55s cubic-bezier(0.22, 1, 0.36, 1);
      }

      .free-shipping-progress__shine {
        position: absolute;
        inset: 0;
        background: linear-gradient(
          110deg,
          transparent 20%,
          rgba(255, 255, 255, 0.55) 45%,
          transparent 70%
        );
        transform: translateX(-120%);
        animation:
          shipping-progress-shine 2.8s
          ease-in-out infinite;
      }

      .free-shipping-progress--complete {
        background:
          radial-gradient(
            circle at top right,
            rgba(84, 150, 101, 0.18),
            transparent 42%
          ),
          linear-gradient(
            135deg,
            #f7fff8 0%,
            #eaf6ed 100%
          );
        border-color: rgba(49, 126, 70, 0.2);
      }

      .free-shipping-progress--complete
      .free-shipping-progress__icon {
        background: linear-gradient(
          135deg,
          #24733a,
          #4ca564
        );
        box-shadow:
          0 6px 14px rgba(36, 115, 58, 0.22);
      }

      .free-shipping-progress--complete
      .free-shipping-progress__eyebrow {
        color: #2b7b40;
      }

      .free-shipping-progress--complete
      .free-shipping-progress__fill {
        background: linear-gradient(
          90deg,
          #26763c,
          #58ad6e
        );
        box-shadow:
          0 0 12px rgba(54, 139, 77, 0.3);
      }

      @keyframes shipping-progress-shine {
        0%,
        35% {
          transform: translateX(-120%);
        }

        70%,
        100% {
          transform: translateX(120%);
        }
      }

      @media screen and (max-width: 749px) {
        .free-shipping-progress {
          margin: 12px 14px 14px;
          padding: 14px;
          border-radius: 14px;
        }

        .free-shipping-progress__icon {
          flex-basis: 38px;
          width: 38px;
          height: 38px;
          border-radius: 11px;
        }

        .free-shipping-progress__icon svg {
          width: 21px;
          height: 21px;
        }

        .free-shipping-progress__main-message {
          font-size: 12px;
        }

        .free-shipping-progress__details {
          margin-top: 12px;
          font-size: 9px;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .free-shipping-progress__fill,
        .free-shipping-progress__icon {
          transition: none;
        }

        .free-shipping-progress__shine {
          animation: none;
        }
      }
    `;

    document.head.appendChild(style);
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);

    if (!dialog) return;

    /*
     * Refs do not cross nested component boundaries,
     * so we query inside the dialog.
     */
    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}